<template>
  <div class="p-3 container">
    <div v-if="!isDone">
      <h2>{{ checkoutType === 'Order' ? $t('checkout.order') : $t('checkout.request') }}</h2>
      <el-card>
        <div slot="header" class="font-weight-bold text-left">
          {{ $t('general.your_data') }}
        </div>
        <div>
          <table class="el-table el-table--striped">
            <tr>
              <th>{{ $t('general.name') }}</th>
              <td>{{ fullName }}</td>
            </tr>
            <tr>
              <th>{{ $t('general.company') }}</th>
              <td>{{ currentUser.get('company') }}</td>
            </tr>
            <tr>
              <th>{{ $t('general.email') }}</th>
              <td>{{ currentUser.get('email') }}</td>
            </tr>
            <tr>
              <th>{{ $t('general.address') }}</th>
              <td>{{ userAddress }}</td>
            </tr>
          </table>
        </div>
      </el-card>
      <el-card class="mt-3">
        <div slot="header" class="font-weight-bold text-left">
          {{ $t('general.details') }}
        </div>
        <div>
          <table class="el-table">
            <tr v-if="isOrder">
              <th>{{ $tc('general.delivery_address', 1) }}</th>
              <th>{{ deliveryAddress }}</th>
            </tr>
            <tr v-if="isOrder">
              <th>{{ $t('checkout.delivery_date') }}</th>
              <th>{{ checkoutDetails.deliveryDate.toLocaleDateString() }}</th>
            </tr>
            <tr v-if="isOrder">
              <th>{{ $t('placeholder.phone') }}</th>
              <th>{{ checkoutDetails.phone }}</th>
            </tr>
            <tr v-if="isOrder && !isEnglish">
              <th>{{ $t('checkout.lkw_ramp') }}</th>
              <th>{{ checkoutDetails.lkw ? $t('general.yes') : $t('general.no') }}</th>
            </tr>
            <tr v-if="checkoutDetails.contactAtLocation">
              <th>{{ $t('checkout.contact_at_location') }}</th>
              <th>{{ checkoutDetails.contactAtLocation }}</th>
            </tr>
            <tr>
              <th>{{ $t('checkout.project') }}</th>
              <th>{{ checkoutDetails.project }}</th>
            </tr>
            <tr v-if="isOrder">
              <th>{{ $t('checkout.adjoining_area') }}</th>
              <th>{{ checkoutDetails.adjoiningArea ? $t('general.yes') : $t('general.no') }}</th>
            </tr>
            <tr v-if="isOrder && checkoutDetails.adjoiningArea">
              <th>{{ $t('checkout.reference_id') }}</th>
              <th>{{ checkoutDetails.referenceId }}</th>
            </tr>
            <tr v-if="isOrder && isEnglish">
              <th>Documents Required</th>
              <th>{{ checkoutDetails.documentsRequired }}</th>
            </tr>
            <tr>
              <th>{{ $t('general.notes') }}</th>
              <th>{{ checkoutDetails.notes }}</th>
            </tr>
            <tr v-if="isOrder && isEnglish">
              <th>Terms of Delivery</th>
              <th>
                <span>
                  FCA:
                  <i v-if="checkoutDetails.termsOfDelivery.fca" class="el-icon-check" />
                  <i v-else class="el-icon-close" />
                </span>
                <span>
                  CIP:
                  <i v-if="checkoutDetails.termsOfDelivery.cip" class="el-icon-check" />
                  <i v-else class="el-icon-close" />
                </span>
                <span v-if="checkoutDetails.termsOfDelivery.cip">
                  Unloading equipment required(for CIP only):
                  <i v-if="checkoutDetails.termsOfDelivery.unloadingEquipment" class="el-icon-check" />
                  <i v-else class="el-icon-close" />
                </span>
                <span>
                  CIF:
                  <i v-if="checkoutDetails.termsOfDelivery.cif" class="el-icon-check" />
                  <i v-else class="el-icon-close" />
                </span>
              </th>
            </tr>
          </table>
        </div>
      </el-card>
      <el-card class="mt-3">
        <div slot="header" class="font-weight-bold text-left">
          {{ $t('pages.cart') }}
        </div>
        <el-card v-if="cart.get('products').length > 0" class="text-left">
          <div slot="header" class="font-weight-bold text-left">
            {{ $tc('products.product', 2) }}
          </div>
          <ProductCartEntry
            v-for="product in cart.get('products')"
            :key="product.id"
            class="mb-1 py-2 border-bottom"
            :product="product"
            read-only
          />
        </el-card>

        <el-card v-if="cart.get('systems').length > 0" class="mt-3 text-left">
          <div slot="header" class="font-weight-bold text-left">
            {{ $tc('systems.system', 2) }}
          </div>
          <SystemCartEntry
            v-for="system in cart.get('systems')"
            :key="system.id"
            class="mb-1 py-2 border-bottom"
            :system="system"
            read-only
          />
        </el-card>
      </el-card>
      <el-button :disabled="isSubmitting || isDone" type="primary" class="my-4 w-100 shadow" @click="onSubmit">
        {{ buttonText }}
      </el-button>
    </div>
    <div v-else class="m-auto text-center">
      <NoResults :text="$t('messages.success_placing', { label: tt })" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex';
import StoreModules from '../../store/store-modules';
import ProductCartEntry from '../../views/cart/components/ProductCartEntry';
import SystemCartEntry from '../../views/cart/components/SystemCartEntry';
import NoResults from '../../components/NoResults';
export default {
  name: 'CheckoutSummaryPage',
  components: { NoResults, SystemCartEntry, ProductCartEntry },
  props: {},
  mounted() {
    if (!this.checkoutDetails.project || !this.checkoutType) {
      this.$router.push({ name: this.$routeNames.CHECKOUT_DETAILS_PAGE_ROUTE });
    }
  },
  destroyed() {},
  data() {
    return {
      isSubmitting: false,
      isDone: false,
    };
  },
  computed: {
    ...mapGetters(StoreModules.USER, ['fullName', 'currentUser', 'language']),
    ...mapGetters(StoreModules.CHECKOUT, ['checkoutType', 'checkoutDetails']),
    ...mapGetters(StoreModules.DELIVERY_ADDRESSES, ['deliveryAddresses']),
    ...mapGetters(StoreModules.CART, ['cart']),
    userAddress() {
      return `${this.currentUser.get('street')}, ${this.currentUser.get('zipCode')} ${this.currentUser.get('city')}`;
    },
    deliveryAddress() {
      let d = this.deliveryAddresses.filter((el) => el.id === this.checkoutDetails.deliveryAddress)[0];
      return `${d.get('street')}, ${d.get('zipCode')} ${d.get('city')}`;
    },
    buttonText() {
      return this.checkoutType === 'Order' ? this.$t('checkout.buy_order') : this.$t('checkout.place_request');
    },
    tt() {
      return this.checkoutType === 'Order' ? this.$t('checkout.order') : this.$t('checkout.request');
    },
    isOrder() {
      return this.checkoutType === 'Order';
    },
    isEnglish() {
      return this.language === 'en';
    },
  },
  methods: {
    ...mapActions(StoreModules.CART, ['loadCart']),
    async onSubmit() {
      this.isSubmitting = true;

      let className = this.checkoutType === 'Order' ? 'CartOrder' : 'CartRequest';
      let obj = new this.$parse.Object(className);

      obj.set('user', this.$parse.User.current());
      obj.set(
        'deliveryAddress',
        this.deliveryAddresses.filter((el) => el.id === this.checkoutDetails.deliveryAddress)[0]
      );
      obj.set('contactAtLocation', this.checkoutDetails.contactAtLocation);
      obj.set('phone', this.checkoutDetails.phone || '');
      obj.set('lkw', this.checkoutDetails.lkw);
      obj.set('project', this.checkoutDetails.project);
      obj.set('deliveryDate', this.checkoutDetails.deliveryDate);
      obj.set('products', this.cart.get('products'));
      obj.set('systems', this.cart.get('systems'));
      obj.set('notes', this.checkoutDetails.notes);

      if (this.isOrder) {
        obj.set('adjoiningArea', this.checkoutDetails.adjoiningArea);
        obj.set('referenceId', this.checkoutDetails.referenceId);

        if (this.isEnglish) {
          obj.set('termsOfDelivery', JSON.stringify(this.checkoutDetails.termsOfDelivery));
          obj.set('documentsRequired', JSON.stringify(this.checkoutDetails.documentsRequired));
        }
      }

      await obj.save();
      this.isSubmitting = false;
      this.isDone = true;

      setTimeout(() => {
        this.loadCart();
      }, 3000);
    },
  },
};
</script>

<style scoped></style>
